import { format } from 'owa-localize';
import getCLPAutoLabel from './getCLPAutoLabel';
import GetAutoLabelResponse, {
    GetAutoLabelResponseStatus,
} from '../../store/schema/clp/GetAutoLabelResponse';
import CLPViewState from 'owa-mail-protection-types/lib/schema/clp/CLPViewState';
import AutoLabelViewState from 'owa-mail-protection-types/lib/schema/clp/AutoLabelViewState';
import ItemId from 'owa-service/lib/contract/ItemId';
import { getLabelFromStore } from './getExistingCLPInfo';
import { trace } from 'owa-trace';
import updateAutoLabelViewState from '../../actions/clp/updateAutoLabelViewState';
import { InfoBarHostViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import applyAutoLabel from './applyAutoLabel';
import {
    logCLPAutoLabelingEnabled,
    logAutoLabelApplied,
    logRecommendedLabelShown,
} from './logCLPDatapoints';
import addRecommendedLabelInfoBar from './addRecommendedLabelInfoBar';
import { shouldApplyAutomaticLabel, shouldApplyRecommendedLabel } from './shouldApplyAutoLabel';
import addInfoBarMessage from 'owa-info-bar/lib/actions/addInfoBarMessage';
import isAutoLabelEnabled from './isAutoLabelEnabled';
import constructCLPAuditAction from './constructCLPAuditAction';
import addAuditActionToViewState from '../../actions/clp/addAuditActionToViewState';
import {
    CLPAuditLogType,
    CLPAuditOperation,
    CLPAuditOperationTrigger,
    CLPAuditSensitivityChange,
} from 'owa-mail-protection-types/lib/schema/clp/CLPAuditAction';

const AUTO_LABEL_TYPES: (keyof AutoLabelViewState)[] = ['automatic', 'recommended'];

function triggerCLPAutoLabeling(
    infobarViewState: InfoBarHostViewState,
    clpViewState: CLPViewState,
    itemId: ItemId,
    content: string
) {
    if (!isAutoLabelEnabled()) {
        return;
    }

    const { autoLabelViewState } = clpViewState;
    if (!autoLabelViewState.shouldKeepGettingAutoLabel) {
        updateAutoLabelViewState(autoLabelViewState, 'inProgressAutoLabelRequest', null);
        updateAutoLabelViewState(autoLabelViewState, 'pendingAutoLabelRequest', null);
        return;
    }

    let { contentOfExistingAutoLabel } = autoLabelViewState;

    if (!autoLabelViewState.inProgressAutoLabelRequest) {
        // Do nothing if content is empty
        if (!content || content == '') {
            return;
        }

        if (content != contentOfExistingAutoLabel) {
            updateAutoLabelViewState(autoLabelViewState, 'inProgressAutoLabelRequest', {
                itemId,
                content,
            });
            getCLPAutoLabel(itemId).then(resp => {
                handleGetAutoLabel(infobarViewState, clpViewState, resp);
            });
        }
    } else {
        updateAutoLabelViewState(autoLabelViewState, 'pendingAutoLabelRequest', {
            itemId,
            content,
        });
    }
}

function handleGetAutoLabel(
    infobarViewState: InfoBarHostViewState,
    clpViewState: CLPViewState,
    resp: GetAutoLabelResponse
) {
    const { autoLabelViewState } = clpViewState;
    logCLPAutoLabelingEnabled(resp);

    if (resp) {
        switch (resp.status) {
            case GetAutoLabelResponseStatus.OK:
                onGetAutoLabelSucceeded(infobarViewState, clpViewState, resp);
                break;
            case GetAutoLabelResponseStatus.BadRequest:
            case GetAutoLabelResponseStatus.Unauthorized:
            case GetAutoLabelResponseStatus.NotFound:
            case GetAutoLabelResponseStatus.TooManyRequests:
            case GetAutoLabelResponseStatus.InternalServerError:
                onGetAutoLabelFailed(autoLabelViewState, resp);
                break;
            default:
                return;
        }
    }

    updateAutoLabelViewState(autoLabelViewState, 'inProgressAutoLabelRequest', null);
    const { pendingAutoLabelRequest } = autoLabelViewState;
    if (pendingAutoLabelRequest) {
        const { itemId, content } = pendingAutoLabelRequest;
        triggerCLPAutoLabeling(infobarViewState, clpViewState, itemId, content);
    }
}

function onGetAutoLabelSucceeded(
    infobarViewState: InfoBarHostViewState,
    clpViewState: CLPViewState,
    resp: GetAutoLabelResponse
) {
    const { autoLabelViewState } = clpViewState;
    let { inProgressAutoLabelRequest } = autoLabelViewState;

    AUTO_LABEL_TYPES.forEach(autoLabelType => {
        let labelToApply = null;
        if (resp[autoLabelType]?.sensitivityLabel) {
            labelToApply = getLabelFromStore(resp[autoLabelType].sensitivityLabel.id);
        }

        updateAutoLabelViewState(autoLabelViewState, autoLabelType, labelToApply);
    });

    const { automatic, recommended } = autoLabelViewState;

    if (shouldApplyAutomaticLabel(clpViewState, automatic)) {
        logAutoLabelApplied();
        addAuditActionToViewState(
            clpViewState,
            constructCLPAuditAction(
                automatic,
                CLPAuditLogType.LabelAction,
                CLPAuditOperation.LabelApplied,
                CLPAuditOperationTrigger.Automatic,
                CLPAuditSensitivityChange.Upgraded,
                clpViewState.selectedCLPLabel
            )
        );
        applyAutoLabel(clpViewState);
        addInfoBarMessage(infobarViewState, 'CLPAutoLabelAutomatic');
    }

    if (shouldApplyRecommendedLabel(clpViewState, recommended)) {
        logRecommendedLabelShown();
        addAuditActionToViewState(
            clpViewState,
            constructCLPAuditAction(
                recommended,
                CLPAuditLogType.PolicyMatch,
                CLPAuditOperation.PolicyMatched,
                CLPAuditOperationTrigger.Recommended,
                null,
                clpViewState.selectedCLPLabel
            )
        );
        addRecommendedLabelInfoBar(infobarViewState, autoLabelViewState);
    }

    if (inProgressAutoLabelRequest) {
        updateAutoLabelViewState(
            autoLabelViewState,
            'contentOfExistingAutoLabel',
            inProgressAutoLabelRequest.content
        );
    }
}

function onGetAutoLabelFailed(autoLabelViewState: AutoLabelViewState, resp: GetAutoLabelResponse) {
    if (resp) {
        trace.warn(
            format(
                'GetAutoLabelRequest failed with code of: {0}; Error Message: {1}',
                resp.status,
                resp.errorMessage
            )
        );
    }
    updateAutoLabelViewState(autoLabelViewState, 'shouldKeepGettingAutoLabel', false);
}

export default triggerCLPAutoLabeling;
